import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import toast from 'react-hot-toast';
import { getErrorString } from '../../utils';
import { fetchMeasurementsApi, fetchUserDashboardConfigApi, fetchUserData } from '../../api/user-dashboard';
import { fetchCurrentLabOrderApi, fetchLabPanelApi } from 'api/lab-order-api';
import { fetchCurrentLabReviewApi } from 'api/lab-reviews-api';

const initialState = {
  data: {},
  config: {},
  measurements: [],
  pending: true,
  journeyStages: [],
  labJourneyStages: [],
  clientData: {},
  userConfirmation: {},
  predictions: [],
  doctorChatSupport: null,
  labsVisibility: true,
  loading: true,
  measurementLoading: true,
  configLoading: true,
  currentLabOrder: {},
  currentLabReview: {},
  currentLabPanels: [],
  viewInstructionInitialToggle: false,
};

export const fetchUserDasboardConfig = createAsyncThunk('user/dashboard/config', async (_, thunkAPI) => {
  try {
    const res = await fetchUserDashboardConfigApi();
    return res;
  } catch (error) {
    toast.error(getErrorString(error));
  }
});

export const fetchUserDashboard = createAsyncThunk('user/dashboard', async ({ activateLoader }) => {
  try {
    const res = await fetchUserData();
    return res;
  } catch (error) {
    toast.error(getErrorString(error));
  }
});

export const fetchCurrentLabOrders = createAsyncThunk('user/lab-orders', async () => {
  try {
    const res = await fetchCurrentLabOrderApi();
    return res;
  } catch (error) {
    toast.error(getErrorString(error));
  }
});

export const fetchCurrentLabPanels = createAsyncThunk('user/lab-panels', async () => {
  try {
    const res = await fetchLabPanelApi();
    return res;
  } catch (error) {
    toast.error(getErrorString(error));
  }
});

export const fetchCurrentLabReview = createAsyncThunk('user/lab-review', async () => {
  try {
    const res = await fetchCurrentLabReviewApi();
    return res;
  } catch (error) {
    toast.error(getErrorString(error));
  }
});

export const fetchUserMeasurements = createAsyncThunk('user/measurements', async (id) => {
  try {
    const res = await fetchMeasurementsApi(id);
    return res;
  } catch (error) {
    toast.error(getErrorString(error));
  }
});

const UserDashboardSlice = createSlice({
  name: 'userdashboard',
  initialState,
  reducers: {
    setJourneyStages: (state, action) => {
      state.journeyStages = action?.payload;
    },
    setUserConfirmation: (state, action) => {
      state.userConfirmation = action?.payload;
    },
    setClientData: (state, action) => {
      state.clientData = action?.payload || {};
    },
    setPending: (state, action) => {
      state.pending = action?.payload;
    },
    setUnreadMessagesCount: (state, action) => {
      state.config.unread_support_chat_messages = action?.payload;
    },
    resetUserConfig: (state) => {
      state.config = {};
      state.configLoading = true;
    },
    updateUserConfig: (state, { payload = {} }) => {
      state.config = { ...(state.config || []), ...payload };
    },
    setPendingTransactionConfig: (state, action) => {
      state.config.pending_transaction = action?.payload?.pending_transaction;
    },
    setViewInstructionToggle: (state, action) => {
      state.viewInstructionInitialToggle = action?.payload;
    },
    setCurrentLabReview: (state, { payload }) => {
      state.currentLabReview = payload;
    },
    removeUserDashbaordData: (state) => {
      state.data = {};
    },
  },
  extraReducers(builder) {
    builder
      .addCase(fetchUserDashboard.pending, (state, action) => {
        if (action.meta?.arg?.activateLoader) {
          state.loading = true;
        }
      })
      .addCase(fetchUserDashboard.fulfilled, (state, action) => {
        state.clientData = action?.payload?.client_patient || {};
        state.data = action?.payload?.data || {};
        state.journeyStages = action?.payload?.journey_stages || [];
        state.labJourneyStages = action?.payload?.lab_journey_stages || [];
        state.userConfirmation = action?.payload?.user_comfirmation || {};
        state.labsVisibility = action?.payload?.lab_records_visibility ?? false;
        state.loading = false;
      })
      .addCase(fetchUserMeasurements.fulfilled, (state, action) => {
        state.measurements = action?.payload?.data || {};
        state.predictions = action?.payload?.weight_loss_predictions || [];
        state.measurementLoading = false;
      })
      .addCase(fetchUserDashboard.rejected, (state) => {
        state.loading = false;
      })
      .addCase(fetchUserMeasurements.rejected, (state) => {
        state.measurementLoading = false;
      })
      .addCase(fetchCurrentLabOrders.fulfilled, (state, action) => {
        state.currentLabOrder = action?.payload?.data ?? {};
      })
      .addCase(fetchCurrentLabPanels.fulfilled, (state, action) => {
        state.currentLabPanels = action?.payload?.data ?? [];
      })
      .addCase(fetchCurrentLabReview.fulfilled, (state, action) => {
        state.currentLabReview = action?.payload?.data ?? {};
      })
      .addCase(fetchUserDasboardConfig.pending, (state) => {
        state.configLoading = true;
      })
      .addCase(fetchUserDasboardConfig.fulfilled, (state, action) => {
        state.config = action?.payload?.data ?? {};
        state.userConfirmation = action?.payload?.data?.user_comfirmation || {};
        state.doctorChatSupport = action?.payload?.data?.mdi_chat_support
          ? 'mdi'
          : action?.payload?.data?.telegramd_chat_support
            ? 'telegramd'
            : action?.payload?.data?.gethealthie_chat_support
              ? 'gethealthie'
              : null;
        state.configLoading = false;
      })
      .addCase(fetchUserDasboardConfig.rejected, (state) => {
        state.configLoading = false;
      });
  },
});

export const {
  setJourneyStages,
  setClientData,
  setPending,
  setUserConfirmation,
  setUnreadMessagesCount,
  resetUserConfig,
  updateUserConfig,
  setPendingTransactionConfig,
  setViewInstructionToggle,
  setCurrentLabReview,
  removeUserDashbaordData,
} = UserDashboardSlice.actions;
export default UserDashboardSlice.reducer;
